import {
  FACEBOOK,
  INSTAGRAM,
  LINKEDIN_ICON,
  OUTLOOK,
  PINTEREST,
  SNAPCHAT,
  TICK_TOK_ICON,
  X_ICON,
  YOUTUBE_ICON,
} from '@ice-web-app/assets';

import { translate } from '@ice-web-app/shared-utils-ssr';

// Define supported platforms
export type SocialMediaPlatform =
  | 'facebook'
  | 'twitter'
  | 'linkedin'
  | 'instagram'
  | 'youtube'
  | 'tiktok'
  | 'pinterest'
  | 'x'
  | 'outlook'
  | 'snapchat';

const defaultLabel = translate({ ns: 'common', key: 'SOCIAL_MEDIA' });

const translatedTitles: Record<string, string> = {
  facebook: translate({ ns: 'common', key: 'FACEBOOK' }),
  twitter: 'X',
  linkedin: translate({ ns: 'common', key: 'LINKEDIN' }),
  instagram: translate({ ns: 'common', key: 'INSTAGRAM' }),
  youtube: translate({ ns: 'common', key: 'YOUTUBE' }),
  tiktok: translate({ ns: 'common', key: 'TIKTOK' }),
  pinterest: translate({ ns: 'common', key: 'PINTEREST' }),
  snapchat: translate({ ns: 'common', key: 'SNAPCHAT' }),
  outlook: translate({ ns: 'common', key: 'OUTLOOK' }),
};

type SocialMediaResult = {
  icon: string | null;
  translatedTitle: string | null;
};

export const socialMediaIcons: Record<SocialMediaPlatform, any> = {
  facebook: FACEBOOK,
  twitter: X_ICON,
  x: X_ICON,
  linkedin: LINKEDIN_ICON,
  outlook: OUTLOOK,
  instagram: INSTAGRAM,
  youtube: YOUTUBE_ICON,
  tiktok: TICK_TOK_ICON,
  pinterest: PINTEREST,
  snapchat: SNAPCHAT,
};

const normalizePlatform = (platform: string): SocialMediaPlatform => {
  const lower = platform.toLowerCase();
  return lower === 'x' ? 'twitter' : (lower as SocialMediaPlatform);
};

export const socialMediaPlatforms: SocialMediaPlatform[] = [
  'facebook',
  'twitter',
  'linkedin',
  'instagram',
  'youtube',
  'tiktok',
  'pinterest',
  'snapchat',
  'outlook',
  'x',
];

const validHostnames: Record<SocialMediaPlatform, string[]> = {
  facebook: ['facebook.com'],
  twitter: ['twitter.com'],
  x: ['x.com'],
  linkedin: ['linkedin.com'],
  instagram: ['instagram.com'],
  youtube: ['youtube.com'],
  tiktok: ['tiktok.com'],
  pinterest: ['pinterest.com'],
  snapchat: ['snapchat.com'],
  outlook: ['outlook.com', 'live.com', 'hotmail.com'],
};

export const getSocialMediaIcon = (input: string, fallbackIcon?: SocialMediaPlatform): SocialMediaResult => {
  const trimmedInput = input?.trim().toLowerCase();

  // Case: check hostname from URL
  try {
    const url = new URL(trimmedInput.startsWith('http') ? trimmedInput : `https://${trimmedInput}`);
    const hostname = url.hostname.toLowerCase();

    // Check if the hostname is exactly one of the valid hostnames
    for (const platform of socialMediaPlatforms) {
      const validHosts = validHostnames[platform] || [];
      // Ensure that the hostname matches exactly one of the valid hostnames
      if (validHosts.some((validHost) => hostname.endsWith(validHost))) {
        const normalized = normalizePlatform(platform);
        return {
          icon: socialMediaIcons[normalized] ?? null,
          translatedTitle: translatedTitles[normalized] ?? null,
        };
      }
    }
  } catch (_e) {
    // If it's not a valid URL, continue and skip the matching
  }

  // Fallback: If no match, return the fallback icon and title
  return {
    icon: fallbackIcon ? (socialMediaIcons[fallbackIcon] ?? null) : null,
    translatedTitle: fallbackIcon ? (translatedTitles[fallbackIcon] ?? defaultLabel) : defaultLabel,
  };
};
